import { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2, Download, Ruler, MapPin, Zap, Satellite, Sparkles, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useGoogleMaps } from "@/hooks/useGoogleMaps";
import { useAuth } from "@/hooks/useAuth";
import { AddressAutocomplete } from "@/components/measurements/AddressAutocomplete";
import { ReportHero } from "@/components/measurements/report/ReportHero";
import { KpiTiles } from "@/components/measurements/report/KpiTiles";
import { WasteTable } from "@/components/measurements/report/WasteTable";
import { LinearMeasurements } from "@/components/measurements/report/LinearMeasurements";
import { MaterialOrderingGuide } from "@/components/measurements/report/MaterialOrderingGuide";
import { PricingControls, type MaterialOption } from "@/components/measurements/report/PricingControls";
import { QuotePreview } from "@/components/measurements/report/QuotePreview";
import { SaveToJobDialog } from "@/components/measurements/SaveToJobDialog";
import { AdditionalTradesBuilder } from "@/components/measurements/AdditionalTradesBuilder";
import { getStaticMapUrl } from "@/utils/roofCalculations";
import { GOOGLE_MAPS_API_KEY } from "@/config/google-maps";
import aerialHomeHero from "@/assets/aerial-home-hero.jpg";
import { Input } from "@/components/ui/input";
import { useOrgTrades } from "@/hooks/useOrgTrades";
import { User } from "lucide-react";

const MATERIALS: MaterialOption[] = [
  { key: "asphalt-30", label: "30-Year Architectural Shingles", pricePerSq: 95 },
  { key: "asphalt-50", label: "50-Year Premium Shingles", pricePerSq: 130 },
  { key: "metal-standing", label: "Standing Seam Metal", pricePerSq: 350 },
  { key: "metal-stone", label: "Stone-Coated Metal", pricePerSq: 280 },
  { key: "tile-clay", label: "Clay Tile", pricePerSq: 450 },
  { key: "flat-tpo", label: "TPO Flat Roof", pricePerSq: 180 },
];

export default function Measurements() {
  const location = useLocation();
  const { user, orgId } = useAuth();
  const { isLoaded } = useGoogleMaps();
  const { orgTrades } = useOrgTrades();

  const DRAFT_KEY = "rg_quick_quote_draft_v1";

  const [address, setAddress] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [selectedTrades, setSelectedTrades] = useState<string[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<any>(null);
  const [generatedAt, setGeneratedAt] = useState<Date | null>(null);
  const [draftLeadId, setDraftLeadId] = useState<string | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);

  // Pricing state
  const [pricePerSq, setPricePerSq] = useState(450);
  const [material, setMaterial] = useState("asphalt-30");
  const [wastePercent, setWastePercent] = useState(20);
  const [complexityPercent, setComplexityPercent] = useState(0);

  // Restore local draft on mount so navigating back doesn't wipe entries
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.address) setAddress(d.address);
        if (d.customerName) setCustomerName(d.customerName);
        if (d.customerPhone) setCustomerPhone(d.customerPhone);
        if (Array.isArray(d.selectedTrades)) setSelectedTrades(d.selectedTrades);
        if (d.selectedLocation) setSelectedLocation(d.selectedLocation);
        if (d.draftLeadId) setDraftLeadId(d.draftLeadId);
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({ address, customerName, customerPhone, selectedTrades, selectedLocation, draftLeadId })
      );
    } catch {}
  }, [address, customerName, customerPhone, selectedTrades, selectedLocation, draftLeadId]);

  useEffect(() => {
    if (location.state?.address) setAddress(location.state.address);
  }, [location.state]);

  const handlePlace = useCallback((p: { address: string; lat: number; lng: number }) => {
    setAddress(p.address);
    setSelectedLocation({ lat: p.lat, lng: p.lng });
  }, []);

  const saveAsLead = async () => {
    if (!user || !orgId) return toast.error("Not signed in");
    if (!customerName.trim() && !address.trim()) return toast.error("Add a name or address first");
    setSavingDraft(true);
    try {
      const payload: any = {
        org_id: orgId,
        contact_name: customerName.trim() || address.trim(),
        contact_phone: customerPhone.trim() || null,
        property_address: address.trim() || null,
        source: "quick_quote",
        status: "new" as const,
        created_by: user.id,
        notes: selectedTrades.length ? `Trades: ${selectedTrades.join(", ")}` : null,
      };

      if (draftLeadId) {
        const { error } = await supabase.from("leads").update(payload).eq("id", draftLeadId);
        if (error) throw error;
        toast.success("Lead updated");
      } else {
        const { data, error } = await supabase.from("leads").insert([payload]).select().single();
        if (error) throw error;
        setDraftLeadId(data.id);
        toast.success("Saved as lead — won't disappear when you go back");
      }
    } catch (e: any) {
      toast.error(e.message || "Save failed");
    } finally {
      setSavingDraft(false);
    }
  };

  const clearDraft = () => {
    setAddress(""); setCustomerName(""); setCustomerPhone("");
    setSelectedTrades([]); setSelectedLocation(null);
    setReport(null); setGeneratedAt(null); setDraftLeadId(null);
    try { localStorage.removeItem(DRAFT_KEY); } catch {}
  };

  const runMeasurement = async () => {
    if (!address.trim()) return toast.error("Enter an address");
    setLoading(true);
    setReport(null);
    try {
      // Ensure a fresh session token is attached (avoids stale 401s)
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session?.access_token) {
        toast.error("Your session expired. Please sign in again.");
        setLoading(false);
        return;
      }
      const { data, error } = await supabase.functions.invoke("google-solar-measurement", {
        body: { address },
        headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const m = data.measurement;
      const lat = selectedLocation?.lat ?? m?.lat ?? 0;
      const lng = selectedLocation?.lng ?? m?.lng ?? 0;
      const facets = m.facets || [];

      // Pitch analysis from facets
      const pitchMap = new Map<string, number>();
      facets.forEach((f: any) => {
        const tilt = f.tiltDegrees ?? 0;
        // Convert degrees to x/12 pitch
        const ratio = Math.round((Math.tan((tilt * Math.PI) / 180) * 12) * 10) / 10;
        const key = `${Math.round(ratio)}/12`;
        pitchMap.set(key, (pitchMap.get(key) || 0) + (f.areaSqFt || 0));
      });
      const totalArea = m.total_area || 1;
      const pitchRows = Array.from(pitchMap.entries()).map(([pitch, sqft]) => ({
        pitch,
        sqft: Math.round(sqft),
        percent: Math.round((sqft / totalArea) * 100),
      }));

      const primaryPitch = pitchRows.sort((a, b) => b.sqft - a.sqft)[0]?.pitch || "—";

      const planeCount = facets.length;
      const complexity: "Simple" | "Moderate" | "Complex" =
        planeCount <= 4 ? "Simple" : planeCount <= 8 ? "Moderate" : "Complex";

      setReport({
        measurementId: m.id,
        address: m.address,
        lat,
        lng,
        totalAreaExact: Number(m.total_area || 0),
        totalSqFt: Math.round(m.total_area),
        squaresExact: Number(m.total_squares || 0),
        squares: Math.round(m.total_squares),
        primaryPitch,
        complexity,
        facets: planeCount,
        planes: facets,
        ridge: m.ridges || 0,
        eave: m.eaves || 0,
        rake: m.rakes || 0,
        hip: m.hips || 0,
        valley: m.valleys || 0,
        pitchRows,
        confidence: 88,
      });
      setGeneratedAt(new Date());
      toast.success("Measurement complete");
    } catch (e: any) {
      toast.error(e.message || "Measurement failed");
    } finally {
      setLoading(false);
    }
  };

  const orderSq = useMemo(
    () => (report ? Math.ceil(report.squares * (1 + wastePercent / 100)) : 0),
    [report, wastePercent]
  );

  // Itemized reference cost (informational only)
  const matChoice = MATERIALS.find((m) => m.key === material) || MATERIALS[0];
  const materialCost = orderSq * matChoice.pricePerSq;
  const laborCost = orderSq * 75;
  const tearoffCost = orderSq * 50;
  const subtotalCost = materialCost + laborCost + tearoffCost;
  const profitAmount = Math.round(orderSq * pricePerSq * (complexityPercent / 100));
  const finalPrice = Math.round(orderSq * pricePerSq * (1 + complexityPercent / 100));

  const measurementData = useMemo(() => {
    if (!report) return null;

      return {
        measurementId: report.measurementId,
      address: report.address,
      lat: report.lat,
      lng: report.lng,
        totalAreaSqFt: report.totalAreaExact ?? report.totalSqFt,
        totalSquares: report.squaresExact ?? report.squares,
        planes: report.planes ?? [],
      roofEdgeData: {
        totalRidgeLength: report.ridge,
        totalEaveLength: report.eave,
        totalRakeLength: report.rake,
        totalHipLength: report.hip,
        totalValleyLength: report.valley,
      },
      wasteFactors: {
        wastePercent,
        cutUpFactor: report.complexity,
        wasteSquares: orderSq,
      },
      avgPitch: report.primaryPitch,
      complexityScore: report.complexity,
      pricingEstimate: {
        material: matChoice.label,
        includeTearoff: true,
        subtotalCost,
        overheadCost: 0,
        profitAmount,
        finalPrice,
      },
    };
  }, [report, wastePercent, orderSq, matChoice.label, subtotalCost, profitAmount, finalPrice]);

  const estimateData = useMemo(
    () =>
      measurementData
        ? {
            totalSquares: orderSq,
            wastePercent,
            materialRate: matChoice.pricePerSq,
            laborRate: 75,
            tearOffCost: tearoffCost,
            dumpFee: 0,
            overheadPercent: 0,
            profitPercent: complexityPercent,
            costSubtotal: subtotalCost,
            overheadAmount: 0,
            profitAmount,
            customerPrice: finalPrice,
          }
        : null,
    [measurementData, orderSq, wastePercent, matChoice.pricePerSq, tearoffCost, complexityPercent, subtotalCost, profitAmount, finalPrice]
  );

  const downloadPdf = async () => {
    if (!report) return;
    toast.info("Generating PDF…");
    try {
      const { data, error } = await supabase.functions.invoke("generate-measurement-pdf", {
        body: {
            measurementData,
        },
      });
      if (error) throw error;
      const html2pdf = (await import("html2pdf.js")).default;
      const el = document.createElement("div");
      el.innerHTML = data.html;
      await html2pdf().set({ filename: data.filename, jsPDF: { unit: "in", format: "letter" } }).from(el).save();
      toast.success("PDF downloaded");
    } catch (e: any) {
      toast.error(e.message || "PDF failed");
    }
  };

  const streetViewUrl = useMemo(() => {
    if (!report || !GOOGLE_MAPS_API_KEY) return undefined;

    const params = new URLSearchParams({
      size: "1200x700",
      location: report.address,
      key: GOOGLE_MAPS_API_KEY,
    });

    return `https://maps.googleapis.com/maps/api/streetview?${params.toString()}`;
  }, [report]);

  const addressBarPhotoUrl = useMemo(() => {
    if (report) return streetViewUrl ?? getStaticMapUrl(report.lat, report.lng);
    if (selectedLocation) return getStaticMapUrl(selectedLocation.lat, selectedLocation.lng);
    return undefined;
  }, [report, streetViewUrl, selectedLocation]);

  return (
    <div className="container mx-auto max-w-7xl space-y-5 px-3 py-4 sm:px-6 sm:py-8">
      {/* Page header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary to-primary/60 shadow-lg shadow-primary/20">
            <Zap className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Quick Quote</h1>
              <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold uppercase tracking-wider">
                Instant
              </span>
            </div>
            <p className="text-muted-foreground text-sm">
              Drop an address — get satellite measurements and a price in under a minute.
            </p>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><Satellite className="w-3.5 h-3.5" /> Google Solar</span>
          <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> ~30 sec</span>
          <span className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> AI pricing</span>
        </div>
      </div>

      {/* Address bar */}
      <div className="rounded-2xl border border-border bg-gradient-to-br from-card to-card/50 p-3 shadow-sm sm:p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          {addressBarPhotoUrl ? (
            <div className="relative h-20 w-full overflow-hidden rounded-xl border border-border bg-muted md:w-44 md:flex-none">
              <img
                src={addressBarPhotoUrl}
                alt={`Property preview for ${report?.address ?? address}`}
                className="h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background/90 to-transparent px-3 py-2">
                <p className="text-xs font-medium text-primary-foreground">
                  {report && streetViewUrl ? "Property photo" : "Roof preview"}
                </p>
              </div>
            </div>
          ) : (
            <div className="hidden md:flex h-20 w-44 flex-none items-center justify-center rounded-xl border border-dashed border-border/60 bg-muted/30 text-muted-foreground">
              <MapPin className="w-6 h-6 opacity-50" />
            </div>
          )}

          <div className="flex-1">
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
              Start with the property address
            </label>
            {isLoaded ? (
              <AddressAutocomplete
                value={address}
                onChange={setAddress}
                onPlaceSelected={handlePlace}
                isLoaded={isLoaded}
              />
            ) : (
              <div className="h-10 rounded-md border border-border bg-muted animate-pulse" />
            )}
          </div>

          <Button
            onClick={runMeasurement}
            disabled={loading}
            size="lg"
            className="h-12 w-full gap-2 rounded-xl font-semibold shadow-md shadow-primary/20 md:min-w-44 md:w-auto md:self-center"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {loading ? "Generating…" : "Start Quick Quote"}
          </Button>
        </div>

        {/* Customer + trades — kept locally so going back doesn't wipe data */}
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
              Customer name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="John Smith"
                className="h-11 pl-9"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
              Phone (optional)
            </label>
            <Input
              type="tel"
              inputMode="tel"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              placeholder="(555) 123-4567"
              className="h-11"
            />
          </div>
        </div>

        {orgTrades.length > 0 && (
          <div className="mt-3">
            <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
              Trades on this quote
            </label>
            <div className="flex flex-wrap gap-2">
              {orgTrades.map((t) => {
                const checked = selectedTrades.includes(t.trade_code);
                return (
                  <button
                    key={t.trade_code}
                    type="button"
                    onClick={() =>
                      setSelectedTrades((s) =>
                        s.includes(t.trade_code) ? s.filter((c) => c !== t.trade_code) : [...s, t.trade_code]
                      )
                    }
                    className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                      checked
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background border-border text-foreground hover:bg-muted"
                    }`}
                  >
                    {t.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          <Button onClick={saveAsLead} disabled={savingDraft} variant="secondary" size="sm" className="gap-1.5">
            {savingDraft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            {draftLeadId ? "Update saved lead" : "Save as lead"}
          </Button>
          {(address || customerName || draftLeadId) && (
            <Button onClick={clearDraft} variant="ghost" size="sm" className="text-muted-foreground">
              Clear & start new
            </Button>
          )}
          {draftLeadId && (
            <span className="text-xs text-muted-foreground self-center">
              Saved — safe to navigate away
            </span>
          )}
        </div>
      </div>

      {!report && !loading && (
        <div className="rounded-2xl border border-border overflow-hidden shadow-xl shadow-primary/5">
          <div className="relative aspect-[16/8] w-full">
            {/* Aerial backdrop */}
            <img
              src={selectedLocation ? getStaticMapUrl(selectedLocation.lat, selectedLocation.lng, 20, "1600x800") : aerialHomeHero}
              alt={selectedLocation ? "Satellite preview of selected property" : "Aerial view of a beautiful home"}
              className="absolute inset-0 h-full w-full object-cover"
            />
            {/* Dark gradient for legibility */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/40" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent" />

            {/* Animated map pin centered on the home */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[60%] flex flex-col items-center pointer-events-none">
              <div className="relative">
                <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping" />
                <span className="absolute -inset-2 rounded-full border-2 border-primary/60 animate-pulse" />
                <div className="relative w-12 h-12 rounded-full bg-primary flex items-center justify-center shadow-2xl shadow-primary/50 ring-4 ring-white/30">
                  <MapPin className="w-6 h-6 text-primary-foreground" fill="currentColor" />
                </div>
              </div>
              <div className="mt-2 w-2 h-2 rounded-full bg-primary/70 blur-[1px]" />
            </div>

            {/* Headline + steps overlay */}
            {!selectedLocation ? (
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                <div className="max-w-2xl">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-md border border-white/20 text-white text-[10px] font-semibold uppercase tracking-wider mb-3">
                    <Sparkles className="w-3 h-3" /> Powered by Google Solar
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-1.5 drop-shadow-lg">
                    Drop a pin. Get a quote.
                  </h3>
                  <p className="text-sm md:text-base text-white/80 max-w-lg drop-shadow-md mb-5">
                    Enter any address above — we'll pull satellite imagery, measure the roof, and price it out in seconds.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { n: 1, label: "Enter address" },
                      { n: 2, label: "AI measures roof" },
                      { n: 3, label: "Send the quote" },
                    ].map((s) => (
                      <div key={s.n} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs font-medium">
                        <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-bold">
                          {s.n}
                        </span>
                        {s.label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                <div className="inline-block px-4 py-3 rounded-xl bg-white/10 backdrop-blur-md border border-white/20">
                  <p className="text-sm font-semibold text-white">{address}</p>
                  <p className="text-xs text-white/70">Click "Start Quick Quote" to generate the satellite report.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {report && generatedAt && (
        <div className="overflow-x-auto pb-2">
          <div className="flex min-w-max items-start gap-4 lg:grid lg:min-w-0 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-6">
            {/* LEFT: Report */}
            <div className="w-[min(100vw-1.5rem,760px)] min-w-[320px] space-y-6 sm:w-[min(100vw-3rem,760px)] lg:w-auto lg:min-w-0">
            <ReportHero
              address={report.address}
              satelliteUrl={getStaticMapUrl(report.lat, report.lng)}
              streetViewUrl={streetViewUrl}
              generatedAt={generatedAt}
              confidence={report.confidence}
              source="Google Solar"
            />
            <KpiTiles
              totalSqFt={report.totalSqFt}
              squares={report.squares}
              orderSquares={orderSq}
              primaryPitch={report.primaryPitch}
              complexity={report.complexity}
            />
            <WasteTable baseSqFt={report.totalSqFt} selectedWaste={wastePercent} />
            <LinearMeasurements
              ridge={report.ridge}
              eave={report.eave}
              rake={report.rake}
              hip={report.hip}
              valley={report.valley}
            />
            <MaterialOrderingGuide
              squares={orderSq}
              ridgeFt={report.ridge}
              eaveFt={report.eave}
              rakeFt={report.rake}
              valleyFt={report.valley}
              wastePercent={wastePercent}
            />
            <p className="text-center text-xs text-muted-foreground pt-2">
              Ballpark estimate — always verify on-site before ordering materials.
            </p>
            </div>

            {/* RIGHT: Pricing + Quote */}
            <div className="w-[360px] min-w-[320px] space-y-4 lg:w-auto lg:min-w-0 lg:sticky lg:top-6 lg:self-start">
            <div className="rounded-xl border border-border bg-card p-4 space-y-4">
              <h3 className="font-semibold">Pricing Controls</h3>
              <PricingControls
                pricePerSq={pricePerSq}
                onPricePerSqChange={setPricePerSq}
                material={material}
                onMaterialChange={(k) => {
                  setMaterial(k);
                  const m = MATERIALS.find((x) => x.key === k);
                  if (m) setPricePerSq(Math.max(pricePerSq, m.pricePerSq * 4));
                }}
                materials={MATERIALS}
                wastePercent={wastePercent}
                onWasteChange={setWastePercent}
                complexityPercent={complexityPercent}
                onComplexityChange={setComplexityPercent}
              />
            </div>
            <QuotePreview
              pricePerSq={pricePerSq}
              squares={report.squares}
              wastePercent={wastePercent}
              complexityPercent={complexityPercent}
              materialCost={materialCost}
              laborCost={laborCost}
              tearoffCost={tearoffCost}
            />
            <AdditionalTradesBuilder baseQuoteTotal={finalPrice} />
            {measurementData && <SaveToJobDialog measurementData={measurementData} estimateData={estimateData} />}
            <Button onClick={downloadPdf} variant="outline" className="w-full gap-2">
              <Download className="w-4 h-4" /> Save & Download PDF Report
            </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
